<template>
  <div v-on="{ input: onInput }">
    <InputLabel
      :label="label"
      :input-id="inputId"
      :required="required"
    />

    <div :class="label ? 'mt-2' : ''">
      <div class="relative">
        <input
          :id="inputId"
          :value="modelValue"
          :type="showPassword ? 'text' : 'password'"
          :name="name"
          :placeholder="placeholder"
          :aria-required="required ? 'true' : undefined"
          :disabled="disabled"
          :readonly="readonly"
          :autocomplete="autocomplete"
          class="-outline-offset-1 bg-surface-canvas block dark:bg-surface-canvas/5 dark:placeholder:text-content-level-500 dark:text-content-inverse focus:-outline-offset-2 focus:outline-2 outline-1 placeholder:text-content-level-400 pr-10 px-3 py-1.5 rounded-md sm:text-sm/6 text-base text-content-level-900 w-full"
          :class="[
            hasError
              ? 'outline-danger-500 focus:outline-danger-500 dark:outline-danger-500'
              : 'outline-focus-level-300 dark:outline-focus-inverse/10 focus:outline-primary dark:focus:outline-primary'
          ]"
          @input="handleInput"
        />

        <button
          type="button"
          class="-translate-y-1/2 absolute cursor-pointer dark:hover:text-content-level-300 hover:text-content-level-600 right-3 text-content-level-400 top-1/2"
          :aria-label="showPassword ? hidePasswordLabel : showPasswordLabel"
          :aria-pressed="showPassword"
          @click="toggleVisibility"
        >
          <EyeSlashIcon
            v-if="showPassword"
            class="size-4"
          />

          <EyeIcon
            v-else
            class="size-4"
          />
        </button>
      </div>

      <p
        v-if="displayError"
        class="mt-1.5 text-danger text-sm"
      >
        {{ displayError }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import type { InputHTMLAttributes } from 'vue'
import InputLabel from './InputLabel.vue'

/**
 * The password control on the surfaces that have not moved to Nuxt UI yet:
 * UserForm.vue, join.vue and accept-invite.vue. The auth surfaces use
 * app/design-system/molecules/PasswordField.molecule.vue instead, which owns
 * the same reveal/hide behaviour over UFormField/UInput. The two exist in
 * parallel while APP-1459..APP-1463 migrate the remaining callers; this file is
 * retired by APP-1470 once nothing references it.
 */
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline'

const props = withDefaults(
  defineProps<{
    /** Native input callback on the outer bubbling boundary. */
    onInput?: InputHTMLAttributes['onInput']
    /** Controlled password value. */
    modelValue?: string
    /** Label displayed above the password field. */
    label?: string
    /** Hint displayed when the password field has no value. */
    placeholder?: string
    /** Whether the password field is required and displays a required marker. */
    required?: boolean
    /** Whether user interaction with the password field is disabled. */
    disabled?: boolean
    /** Whether the password field can be viewed but not edited. */
    readonly?: boolean
    /** HTML form field name assigned to the password input. */
    name?: string
    /** HTML identifier assigned to the password input. */
    id?: string
    /** HTML autocomplete hint assigned to the password input. */
    autocomplete?: string
    /** Minimum character length enforced by the native `minlength` attribute. */
    minlength?: number
    /** Validation message displayed below the password field. */
    error?: string
    /** Accessible label for the toggle button when the password is hidden. */
    showPasswordLabel: string
    /** Accessible label for the toggle button when the password is visible. */
    hidePasswordLabel: string
  }>(),
  {
    autocomplete: 'current-password',
    disabled: false,
    error: '',
    id: '',
    label: '',
    minlength: 8,
    modelValue: '',
    name: 'password',
    onInput: undefined,
    placeholder: '',
    readonly: false,
    required: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const showPassword = ref(false)

function toggleVisibility() {
  showPassword.value = !showPassword.value
}

function handleInput(event: Event) {
  if (event.target instanceof HTMLInputElement) {
    emit('update:modelValue', event.target.value)
  }
}

const displayError = computed(() => props.error)
const hasError = computed(() => displayError.value !== undefined && displayError.value !== '')

const generatedId = useId()
const inputId = computed(() => props.id || generatedId)

// `minlength` renders no native attribute; exposed for callers that
// still read the configured threshold off the instance.
defineExpose({ minlength: computed(() => props.minlength) })
</script>
